import { useCallback, useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { Outlet, useOutletContext } from 'react-router';

import type { FVTReferralDetails, FVTReferralFamilyMemberCreateRequest } from '@customTypes/referrals';
import { createFVTReferralFamilyMember, getFVTReferral } from '@store/actions/referrals';

export interface ReferralContext {
  referral: FVTReferralDetails;
  createFamilyMember: (familyMember: FVTReferralFamilyMemberCreateRequest) => Promise<void>;
}

export interface ReferralRootProps {
  fvtReferralUUID: string | null;
}

const LoadingSpinner = () => (
  <div className='page_loading_spinner_wrapper text-primary'>
    <Spinner animation='border' />
  </div>
);

export const ReferralRoot: React.FC<ReferralRootProps> = props => {
  const { fvtReferralUUID } = props;

  const [referral, setReferral] = useState<FVTReferralDetails>();
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    if (!fvtReferralUUID) return;

    let isMounted = true;
    setReferral(undefined);
    setApiError(false);

    const fetchReferral = async () => {
      try {
        const apiReferral = await getFVTReferral(fvtReferralUUID);

        if (isMounted) {
          setReferral(apiReferral);
        }
      } catch {
        if (isMounted) {
          setApiError(true);
        }
      }
    };

    fetchReferral();

    return () => {
      isMounted = false;
    };
  }, [fvtReferralUUID]);

  const handleCreateFamilyMember = useCallback(
    async (familyMember: FVTReferralFamilyMemberCreateRequest) => {
      if (!fvtReferralUUID) return;

      const newFamilyMember = await createFVTReferralFamilyMember(fvtReferralUUID, familyMember);

      setReferral(prev => {
        if (!prev) {
          return prev;
        }

        const previousFamilyMembers = (prev?.family_members || []).filter(
          familyMember => familyMember.uuid !== newFamilyMember.uuid,
        );

        return {
          ...prev,
          family_members: [...previousFamilyMembers, newFamilyMember],
        };
      });
    },
    [fvtReferralUUID],
  );

  if (apiError) {
    return (
      <Alert variant='danger' className='mt-4'>
        <Alert.Heading as='h2'>Error loading your information</Alert.Heading>
        <p>We encountered an error while looking up your referral for family variant testing.</p>

        <p>
          Please reload the page. If the problem persists, please contact us at{' '}
          <a href='mailto:support@myome.com'>support@myome.com</a>.
        </p>
      </Alert>
    );
  }

  if (!referral) {
    return <LoadingSpinner />;
  }

  const context: ReferralContext = {
    createFamilyMember: handleCreateFamilyMember,
    referral,
  };

  return <Outlet context={context} />;
};

export const useReferralContext = (): ReferralContext => useOutletContext<ReferralContext>();
